/* AXIS foil fit, product-page version (ported from Live2Kite, 2026-09-25).
 *
 * Same engine, same rules, same catalogue as /pages/foil-fit. It reads the three assets
 * the page already loads, so it cannot drift from the page: one rule set, one answer.
 *
 * CONSTRAINT FIRST, LIST SECOND, and that is the whole design. Measured before building:
 * a Skinny Link rear mounts to 1 of 26 fuselages and a Fatty e-foil mast to 3 of 26,
 * which is decisive, while a Black Standard fuselage takes 60 of 66 front wings, which
 * as a list is wallpaper. So every part gets ONE TRUE SENTENCE about what it needs, and
 * a list only when the list is short enough to be worth reading.
 *
 * It is silent on any product not in the fit table: 162 of the 483 active products carry
 * one, and a board, a bag or an Ozone wing must render nothing at all rather than an
 * empty box.
 *
 * The sentence is DERIVED, never hand-written per product, so it tracks the catalogue.
 */
(function () {
  'use strict';

  // A hard constraint is worth opening for; "fits nearly everything" is not.
  var OPEN_BELOW = 6;

  function famOf(rules, fit, cat, p) {
    var f = null;
    if (cat === 'fuselage') f = fit.fuselageFamily(rules, p.title);
    else if (cat === 'rear wing') f = fit.rearFamily(rules, p.title);
    else if (cat === 'mast') f = fit.mastFamily(rules, p.title);
    return (f && f.family) || p.family || null;
  }
  function label(p) {
    return p.title.replace(/^AXIS Foils\s+/i, '').replace(/^AXIS\s+/i, '');
  }
  // FATTY is written in capitals (Evan, 2026-09-20; the pricelists read "FATTY FUSELAGES
  // (compatible with FATTY masts and FATTY doodad)"). Applied HERE, where family names
  // become words on the page, and NOT to the family names themselves: those are lookup
  // keys, and mastGroups matches them exactly, so renaming them would silently empty a
  // mast group.
  function show(name) { return String(name).replace(/\bFatty\b/g, 'FATTY'); }
  function list(a) {
    a = a.map(show);
    if (a.length === 1) return a[0];
    if (a.length === 2) return a[0] + ' and ' + a[1];
    return a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1];
  }

  /**
   * One true sentence about a set of counterparts.
   *
   * Phrased from what actually fits rather than from a rule id, so a rule change moves
   * the sentence with it. "Every X except the Y" is only used when naming the exceptions
   * is SHORTER than naming what fits, because "fits everything except nothing" is not a
   * sentence anybody wants.
   */
  function describe(fitting, all, noun, nounPlural, rules, fit, cat) {
    if (!fitting.length) return 'We do not have a compatible ' + noun + ' listed just now.';
    if (fitting.length === 1) return 'Only fits the ' + label(fitting[0]) + '.';
    if (fitting.length === all.length) return 'Fits every ' + noun + ' we sell.';

    // A FAMILY IS NOT ALL-OR-NOTHING, and treating it as one printed a false sentence.
    // BSC 740/810/890 are Black-system wings and BSC 970/1060/1120 are Red, by explicit
    // rule, so on a Fatty fuselage three BSC fit and three do not. Counting BSC as
    // "fitting" because some of it does told a BSC 970 owner their wing was fine.
    var tally = {};
    all.forEach(function (p) {
      var f = famOf(rules, fit, cat, p) || 'Other';
      tally[f] = tally[f] || { fit: 0, total: 0 };
      tally[f].total++;
    });
    fitting.forEach(function (p) {
      var f = famOf(rules, fit, cat, p) || 'Other';
      if (tally[f]) tally[f].fit++;
    });
    var none = [], part = [];
    Object.keys(tally).forEach(function (f) {
      if (tally[f].fit === 0) none.push(f);
      else if (tally[f].fit < tally[f].total) part.push(f);
    });

    var tail = part.length ? ', and some ' + list(part) + ' sizes' : '';
    if (none.length && none.length <= 3) {
      return 'Fits every ' + noun + ' except the ' + list(none) + tail + '.';
    }
    if (!none.length && part.length) {
      return 'Fits every ' + noun + ' except some ' + list(part) + ' sizes.';
    }
    var yes = Object.keys(tally).filter(function (f) { return tally[f].fit === tally[f].total; });
    if (yes.length && yes.length <= 3 && !part.length) return 'Fits the ' + list(yes) + ' ' + nounPlural + '.';
    return 'Fits ' + fitting.length + ' of the ' + all.length + ' ' + nounPlural + ' we sell.';
  }

  /**
   * What this product needs, by what it IS. A mast and a rear relate to the FUSELAGE
   * (Evan, 2026-09-19), so those two ask about fuselages and nothing else; a front wing
   * asks which fuselage; a fuselage is the hub and answers for all three.
   */
  function summarise(rules, fit, part, parts) {
    var G = function (c) { return parts.filter(function (p) { return p.category === c; }); };
    var fus = G('fuselage');
    var out = { lines: [], rows: [], noun: '' };

    if (part.category === 'front wing') {
      if (!part.system) {
        out.lines.push('This is an older wing and we do not have it on the fit table yet, so we will not guess at what it takes. Get in touch through axisfoils.com/pages/contact-us and we will work it out with you.');
        return out;
      }
      var ok = fus.filter(function (x) { return fit.fuselageFits(rules, part, x).ok; });
      out.lines.push(describe(ok, fus, 'fuselage', 'fuselages', rules, fit, 'fuselage'));
      out.lines.push('Any mast and rear wing that works with that fuselage will work with this wing.');
      out.rows = ok; out.noun = 'Fuselages that fit';
      return out;
    }
    if (part.category === 'mast') {
      var okm = fus.filter(function (x) { return fit.mastFits(rules, x, part, null, null).ok; });
      out.lines.push(describe(okm, fus, 'fuselage', 'fuselages', rules, fit, 'fuselage'));
      // An aluminium mast fits, but needs a doodad. The sentence said "fits every
      // fuselage" and stopped, which is true and leaves somebody a part short.
      var needs = {};
      okm.forEach(function (x) {
        (fit.mastFits(rules, x, part, null, null).reasons || []).forEach(function (r) { needs[r] = 1; });
      });
      Object.keys(needs).forEach(function (r) { out.lines.push(r); });
      out.rows = okm; out.noun = 'Fuselages it bolts to';
      return out;
    }
    if (part.category === 'rear wing') {
      var okr = fus.filter(function (x) { return fit.rearFits(rules, x, part).ok; });
      var direct = fus.filter(function (x) { return fit.rearFits(rules, x, part).severity === 'ok'; });
      out.lines.push(describe(okr, fus, 'fuselage', 'fuselages', rules, fit, 'fuselage'));
      if (direct.length && direct.length < okr.length) {
        out.lines.push('It bolts straight onto ' + direct.length + ' of those; the rest need an adapter.');
      }
      out.rows = okr; out.noun = 'Fuselages it mounts to';
      return out;
    }
    if (part.category === 'fuselage') {
      var wings = G('front wing').filter(function (x) { return x.system && fit.fuselageFits(rules, x, part).ok; });
      var masts = G('mast').filter(function (x) { return fit.mastFits(rules, part, x, null, null).ok; });
      var rears = G('rear wing').filter(function (x) { return fit.rearFits(rules, part, x).ok; });
      out.lines.push(describe(wings, G('front wing').filter(function (x) { return x.system; }), 'front wing', 'front wings', rules, fit, 'front wing'));
      out.lines.push('Masts: ' + describe(masts, G('mast'), 'mast', 'masts', rules, fit, 'mast').replace(/^Fits /, 'fits ').replace(/^Only fits /, 'only fits '));
      out.lines.push('Rear wings: ' + describe(rears, G('rear wing'), 'rear wing', 'rear wings', rules, fit, 'rear wing').replace(/^Fits /, 'fits ').replace(/^Only fits /, 'only fits '));
      // The hub answers three questions in sentences. A 60-wing list is wallpaper, so
      // list whichever of the three is short enough to be worth reading. The front wings
      // are included here on purpose: on a Red fuselage that set is 9 of 60, which is the
      // single most useful thing the page can show.
      var sets = [
        { r: wings, n: 'Front wings it takes', all: G('front wing').filter(function (x) { return x.system; }).length },
        { r: masts, n: 'Masts it takes', all: G('mast').length },
        { r: rears, n: 'Rear wings it takes', all: G('rear wing').length }
      ].filter(function (x) { return x.r.length && x.r.length < x.all && x.r.length <= 12; })
        .sort(function (a, b) { return a.r.length - b.r.length; });
      if (sets.length) { out.rows = sets[0].r; out.noun = sets[0].n; }
      return out;
    }
    return null;
  }

  function render(root, part, rules, fit, parts) {
    var s = summarise(rules, fit, part, parts);
    if (!s) return;
    var el = function (t, c, x) { var e = document.createElement(t); if (c) e.className = c; if (x != null) e.textContent = x; return e; };
    var box = el('div', 'fitmini');
    box.appendChild(el('h3', 'fitmini-head', 'Will it fit your setup?'));
    s.lines.forEach(function (t) { box.appendChild(el('p', 'fitmini-line', t)); });

    // A list that is the whole catalogue adds nothing the sentence did not already say.
    var everything = s.rows.length && s.rows.length === parts.filter(function (p) {
      return p.category === s.rows[0].category;
    }).length;
    if (s.rows.length && !everything) {
      var d = el('details', 'fitmini-drawer');
      // Open by default only where the constraint is hard, which is the case somebody
      // needs to see before they buy: a Ti Link rear, a Fatty mast.
      if (s.rows.length < OPEN_BELOW) d.open = true;
      var sum = el('summary', 'fitmini-sum');
      sum.appendChild(el('span', null, s.noun));
      sum.appendChild(el('span', 'fitmini-n', String(s.rows.length)));
      d.appendChild(sum);
      var ul = el('ul', 'fitmini-list');
      s.rows.slice(0, 40).forEach(function (p) {
        var li = el('li', null);
        var a = el('a', null, label(p));
        a.href = '/products/' + p.handle;
        li.appendChild(a);
        ul.appendChild(li);
      });
      d.appendChild(ul);
      box.appendChild(d);
    }
    var foot = el('p', 'fitmini-foot');
    var a = el('a', null, 'Check a different foil');
    a.href = (document.getElementById('FoilFitMini') || { getAttribute: function () { return null; } }).getAttribute('data-fit-url') || '/pages/foil-fit';
    foot.appendChild(a);
    box.appendChild(foot);
    root.appendChild(box);
  }

  function boot() {
    var root = document.getElementById('FoilFitMini');
    if (!root) return;
    var handle = root.getAttribute('data-handle');
    if (!handle) return;
    Promise.all([
      fetch(root.getAttribute('data-parts')).then(function (r) { return r.json(); }),
      fetch(root.getAttribute('data-rules')).then(function (r) { return r.json(); })
    ]).then(function (res) {
      if (!window.AxisFit) return;
      var parts = res[0].parts;
      var part = parts.filter(function (p) { return p.handle === handle; })[0];
      // Not in the fit table: a board, a bag, an Ozone wing. Render NOTHING.
      if (!part) return;
      render(root, part, res[1], window.AxisFit, parts);
    }).catch(function (e) { if (window.console) console.error('foil fit mini:', e); });
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { summarise: summarise, describe: describe };
  else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
